import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import { Link, useHistory } from "react-router-dom";
import io from "socket.io-client";
import toast from "react-hot-toast";
import moment from "moment";
import {
  Formik,
  Form,
  FieldArray,
  Field,
  useFormikContext,
  ErrorMessage,
} from "formik";
import * as Yup from "yup";
import "../../styles/LiveSession.css";
import "../../styles/OutletLiveSession.css";
import "../../styles/AppLogo.css";
import { AuthContext } from "../../context/AuthContext";
import LiveSessionServices from "../../services/LiveSessionServices";
import SettingServices from "../../services/SettingServices";
import {
  getYantraImageUrl,
  useOutletTemplateImages,
} from "../../utils/yantraImageTemplate";
import Loader from "../../components/Loader";
import NoData from "../../components/NoData";
import AppLogo from "../../components/AppLogo";
import {
  getStoredActiveGameTab,
  setStoredActiveGameTab,
  LIVE_YANTRA_IMAGE_URL,
  SILVER_COIN_RUPEE_VALUE,
} from "../../utils/utils";
import { AiOutlineClose } from "react-icons/ai";
import { FaPrint } from "react-icons/fa";
import tenorGif from "../../assets/tenor.gif";
import silverCoinGif from "../../assets/silvercoin.jpeg";
import JackpotImage from "../../assets/101728963.jfif";
import JackpotImage2 from "../../assets/829075774018b36ebb587.gif";

/** Outlet live: one toast slot, top-center (replaces previous). */
const OUTLET_LIVE_TOAST_ID = "outlet-live-session-toast";
const emitToastSuccess = toast.success.bind(toast);
const emitToastError = toast.error.bind(toast);
const outletToast = {
  success: (message, options = {}) =>
    emitToastSuccess(message, {
      id: OUTLET_LIVE_TOAST_ID,
      position: "top-center",
      duration: 3000,
      ...options,
    }),
  error: (message, options = {}) =>
    emitToastError(message, {
      id: OUTLET_LIVE_TOAST_ID,
      position: "top-center",
      duration: 4000,
      ...options,
    }),
};

/**
 * Outlet-only image template override.
 * Lifted to client/src/utils/yantraImageTemplate.js so the same override
 * applies on every screen the outlet user sees (live session, all draw, etc.)
 * without re-fetching or extra prop drilling.
 */
const getYantraImage = getYantraImageUrl;

/** First yantra from a today-last-draw row (same shape as Last Draw grid). */
const getFirstWinnerFromHistoryItem = (item) => {
  if (!item || typeof item !== "object") return null;
  const winners =
    Array.isArray(item?.winner_yantras) && item.winner_yantras.length > 0
      ? item.winner_yantras
      : [
          {
            yantra_name: item?.yantra_name,
            yantra_img: item?.yantra_img,
            yantra_id: item?.winner_yantra,
          },
        ];
  const w = winners[0];
  if (!w) return null;
  const yantra_id = w.yantra_id ?? w.id;
  const yantra_name = w.yantra_name || w.name || "";
  const yantra_img = w.yantra_img ?? w.img;
  if (!yantra_name && yantra_id == null) return null;
  return { yantra_id, yantra_name: yantra_name || "Yantra", yantra_img };
};

/**
 * Scan / win print: API returns totalWinAmount in rupees.
 * Silver coins = win amount ÷ ₹100 — not total ÷ winUnitAmount (JACKPOTX winUnit is payout per
 * bet unit, e.g. ₹300 for 3x, which wrongly gave ₹600/300 = 2 instead of 6 coins).
 */
const silverCoinsFromWinPayload = (d) => {
  if (!d) return 0;
  const total = Number(d.totalWinAmount ?? 0);
  if (!Number.isFinite(total) || total <= 0) return 0;
  return Math.max(0, Math.round(total / SILVER_COIN_RUPEE_VALUE));
};

/** Player / slip owner for receipts (scan payload or place-bet row), not the logged-in outlet user. */
const slipOwnerDisplay = (row) => {
  if (!row || typeof row !== "object") return "—";
  const u = String(row.user_username ?? "").trim();
  const n = String(row.user_name ?? "").trim();
  const g = row.user_game_id;
  if (u) return u;
  if (n) return n;
  if (g != null && String(g).trim() !== "") return `[${String(g).trim()}]`;
  const id = row.user_id;
  if (id != null && String(id).trim() !== "") return `#${String(id).trim()}`;
  return "—";
};

/** Memoizes yantra image URLs and preloads images. Must be used in a component (not callback). */
const YantraImagesLoader = ({ values, templateVersion, children }) => {
  const yantraImages = useMemo(
    () =>
      (values?.yantra_list || []).map((y) => ({
        ...y,
        imgSrc: getYantraImage(y.yantra_img, y.yantra_id),
      })),
    // templateVersion bumps when the assigned image template loads, so the
    // memoized URLs refresh exactly once instead of re-running on every render.
    [values?.yantra_list, templateVersion],
  );

  useEffect(() => {
    if (!values?.yantra_list) return;
    values.yantra_list.forEach((y) => {
      const img = new Image();
      img.src = getYantraImage(y.yantra_img, y.yantra_id);
    });
  }, [values?.yantra_list, templateVersion]);

  return children(yantraImages);
};

/** Syncs total_bet and amount into form state when yantra counts change (useEffect to avoid infinite loop). */
const SyncTotalBetAmount = () => {
  const { values, setFieldValue } = useFormikContext();
  const list = values.yantra_list || [];
  const countsKey = JSON.stringify(
    list.map((y) => Number(y.yantra_count) || 0),
  );

  useEffect(() => {
    const totalBet = list.reduce(
      (sum, y) => sum + (Number(y.yantra_count) || 0),
      0,
    );
    const amount = list.reduce(
      (sum, y) =>
        sum + (Number(y.yantra_count) || 0) * (Number(y.yantra_amount) || 0),
      0,
    );
    setFieldValue("total_bet", totalBet);
    setFieldValue("amount", amount);
  }, [countsKey, setFieldValue]); // list not in deps: use countsKey so effect runs only when counts change

  return null;
};

/** Auto focus the first yantra input once, after yantras load. */
const AutoFocusFirstYantraInput = ({ enabled, inputRef }) => {
  const { values } = useFormikContext();
  const didFocusRef = useRef(false);
  const listLen = Array.isArray(values?.yantra_list)
    ? values.yantra_list.length
    : 0;

  useEffect(() => {
    if (!enabled) return;
    if (didFocusRef.current) return;
    if (listLen <= 0) return;

    didFocusRef.current = true;
    setTimeout(() => {
      const el = inputRef?.current;
      if (el && typeof el.focus === "function") {
        el.focus();
      }
    }, 0);
  }, [enabled, listLen, inputRef]);

  return null;
};

// Yantra slideshow (right panel). Optional spotlightYantra = last-draw winner (pause carousel + effect).
const YantraSlideshow = ({ yantras, spotlightYantra }) => {
  const baseList = Array.isArray(yantras) ? yantras : [];
  const spotlight =
    spotlightYantra &&
    typeof spotlightYantra === "object" &&
    (spotlightYantra.yantra_name || spotlightYantra.yantra_id != null)
      ? {
          ...spotlightYantra,
          imgSrc:
            spotlightYantra.imgSrc ??
            getYantraImage(
              spotlightYantra.yantra_img,
              spotlightYantra.yantra_id,
            ),
        }
      : null;

  const list = spotlight ? [spotlight] : baseList;
  const len = list.length;

  const slides = useMemo(() => {
    if (len <= 0) return [];
    // Clone first slide at end for seamless loop
    return [...list, list[0]];
  }, [list, len]);

  const [index, setIndex] = useState(0);
  const [enableTransition, setEnableTransition] = useState(true);

  useEffect(() => {
    setIndex(0);
    setEnableTransition(true);
  }, [len, spotlight?.yantra_id, spotlight?.yantra_name]);

  useEffect(() => {
    if (spotlight) return;
    if (len <= 1) return;

    const id = setInterval(() => {
      setEnableTransition(true);
      setIndex((i) => i + 1);
    }, 2000);

    return () => clearInterval(id);
  }, [len, spotlight]);

  const handleTransitionEnd = () => {
    if (len <= 0) return;
    if (index === len) {
      // Snap back to first slide without animation.
      setEnableTransition(false);
      setIndex(0);
      // Re-enable transition next tick for upcoming slides.
      setTimeout(() => setEnableTransition(true), 20);
    }
  };

  if (len <= 0) {
    return (
      <div className="outlet-yantra-slideshow outlet-yantra-slideshow-empty">
        <div className="outlet-yantra-slide-name">No yantra</div>
      </div>
    );
  }

  return (
    <div
      className={`outlet-yantra-slideshow${spotlight ? " outlet-yantra-slideshow--spotlight" : ""}`}
    >
      {spotlight ? (
        <div className="outlet-yantra-spotlight-badge" aria-hidden>
          Winning Yantra
        </div>
      ) : null}
      <div className="outlet-yantra-slideshow-viewport">
        <div
          className="outlet-yantra-slideshow-track"
          style={{
            transform: `translateX(-${index * 100}%)`,
            transition: enableTransition
              ? "transform 550ms ease-in-out"
              : "none",
          }}
          onTransitionEnd={handleTransitionEnd}
        >
          {slides.map((y, idx) => {
            const imgSrc =
              y?.imgSrc ?? getYantraImage(y?.yantra_img, y?.yantra_id);

            return (
              <div
                className={`outlet-yantra-slide ${idx === index ? "is-active" : ""}${spotlight ? " outlet-yantra-slide--spotlight" : ""}`}
                key={`${y?.yantra_id || idx}-${idx}`}
              >
                <div className="outlet-yantra-slide-inner">
                  <img
                    className="outlet-yantra-slide-img"
                    loading="lazy"
                    src={imgSrc}
                    alt={y?.yantra_name || "Yantra"}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = getYantraImage(null);
                    }}
                  />
                  <div className="outlet-yantra-slide-name">
                    {y?.yantra_name || "Yantra"}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const OutletLiveSession = () => {
  const routerHistory = useHistory();
  const liveSSApi = new LiveSessionServices();
  const settingApi = new SettingServices();
  const [settingData, setSettingData] = useState(null);
  const firstYantraInputRef = useRef(null);
  const yantraInputRefs = useRef([]);
  const yantraGridInnerRef = useRef(null);
  const barcodeInputRef = useRef(null);
  const lastYantraValueRef = useRef({}); // fieldName -> last manual digits
  const [showWalletBL, setShowWalletBL] = useState(false);
  const [allYantraInputUpdate, setAllYantraInputUpdate] = useState("");

  const focusFirstYantraInput = useCallback(() => {
    const el = firstYantraInputRef.current;
    if (el && typeof el.focus === "function") {
      el.focus();
    }
  }, []);

  const getYantraGridCols = useCallback(() => {
    try {
      const el = yantraGridInnerRef.current;
      if (!el) return 5;
      const cols = window.getComputedStyle(el).gridTemplateColumns || "";
      const count = cols.split(/\s+/).filter(Boolean).length;
      return count > 0 ? count : 5;
    } catch (_) {
      return 5;
    }
  }, []);

  const focusYantraInputAt = useCallback((nextIdx) => {
    const el = yantraInputRefs.current?.[nextIdx];
    if (el && typeof el.focus === "function") {
      el.focus();
    }
  }, []);

  const { user, logout } = useContext(AuthContext);
  const [profile, setProfile] = useState(user);
  const [liveSessionNote1, setLiveSessionNote1] = useState("");
  const [liveSessionNote2, setLiveSessionNote2] = useState("");
  const [winningSlipNote, setWinningSlipNote] = useState("");
  const currentFormValuesRef = useRef(null);
  const [barcodeInput, setBarcodeInput] = useState("");
  const [scanResultModalOpen, setScanResultModalOpen] = useState(false);
  const [scanResultData, setScanResultData] = useState(null);
  const scanResultModalOpenRef = useRef(false);
  /** Same barcode must not hit scan-bar-code twice in parallel (gun + input Enter both fire). */
  const barcodeScanFlightRef = useRef(null);
  useEffect(() => {
    scanResultModalOpenRef.current = Boolean(scanResultModalOpen);
  }, [scanResultModalOpen]);

  const closeScanResultModal = useCallback(() => {
    setScanResultModalOpen(false);
    setScanResultData(null);
    setBarcodeInput("");
    setTimeout(() => {
      focusFirstYantraInput();
    }, 0);
  }, [focusFirstYantraInput]);

  const isLikelySlipBarcode = useCallback((digits) => {
    // Slip barcode is generated on server as: unixSeconds(10 digits) + random(2 digits) => 12 digits
    const s = String(digits || "").trim();
    if (!/^[0-9]{12}$/.test(s)) return false;
    const ts = Number(s.slice(0, 10));
    if (!Number.isFinite(ts)) return false;
    const nowSec = Math.floor(Date.now() / 1000);
    // Slip validity is short; keep window tight so large yantra counts don't get mis-detected.
    const thirtyDays = 30 * 24 * 60 * 60;
    const oneDay = 24 * 60 * 60;
    return ts >= nowSec - thirtyDays && ts <= nowSec + oneDay;
  }, []);

  useEffect(() => {
    if (user) setProfile(user);
  }, [user]);

  /**
   * Outlet users only: load the active assigned image template once. The hook
   * stores the override in module scope (shared with All Draw / other screens)
   * and returns a version number so memoized image lists below refresh exactly
   * once when the override resolves. Admin edits do NOT push live to this
   * screen — that's the required performance/stability behavior.
   */
  const templateImageVersion = useOutletTemplateImages(user);

  // --- Barcode scan handling (gun or manual) ---
  const handleBarcodeSubmit = useCallback(
    async (codeRaw, opts = {}) => {
      const code = (codeRaw || barcodeInput || "").trim();
      const clearInput =
        opts?.clearInput === undefined ? true : Boolean(opts.clearInput);
      if (!code) {
        outletToast.error("Please enter barcode");
        return;
      }
      // bar_code (12 digits), receipt suffix (…_XXXX), or full YT_PLACEBET_… uid
      if (!/^[0-9A-Za-z_-]{8,72}$/.test(code)) {
        outletToast.error("Invalid barcode format");
        return;
      }

      const inflight = barcodeScanFlightRef.current;
      if (inflight?.code === code) {
        await inflight.promise;
        return;
      }

      const run = (async () => {
        try {
          const resp = await liveSSApi.scanBarCode(code);
          if (!resp) return;
          if (resp.isSuccess && resp.data) {
            const payload = resp.data || {};
            const enrichedPayload = {
              ...payload,
              outlet_name: profile?.name || "Yantra",
            };
            setScanResultData({
              ...enrichedPayload,
              message: resp.message || "",
              scannedCode: code,
            });
            setScanResultModalOpen(true);
            if (clearInput) setBarcodeInput("");
            if (Number(enrichedPayload.totalWinAmount || 0) > 0) {
              openWinPrintViewWithAutoPrint(enrichedPayload, true);
            }
            const newWalletRaw = payload.finalWalletBalance;
            if (newWalletRaw != null && !Number.isNaN(Number(newWalletRaw))) {
              const newWallet = Number(newWalletRaw);
              setInitialValues((prev) => ({
                ...prev,
                wallet_balance: newWallet,
              }));
              if (formikRef.current?.setFieldValue) {
                formikRef.current.setFieldValue("wallet_balance", newWallet);
              }
            }
          } else {
            setScanResultData({
              message: resp?.message || "Slip not found for this barcode",
              scannedCode: code,
            });
            setScanResultModalOpen(true);
            if (clearInput) setBarcodeInput("");
          }
        } catch (err) {
          const msg =
            err?.response?.data?.message ||
            err?.message ||
            "Failed to scan barcode";
          setScanResultData({
            message: msg,
            scannedCode: code,
          });
          setScanResultModalOpen(true);
          if (clearInput) setBarcodeInput("");
        }
      })();

      barcodeScanFlightRef.current = { code, promise: run };
      try {
        await run;
      } finally {
        if (barcodeScanFlightRef.current?.promise === run) {
          barcodeScanFlightRef.current = null;
        }
      }
    },
    [barcodeInput, liveSSApi, profile],
  );

  // Barcode gun support: capture scan even when input isn't focused.
  // Most scanners type quickly then send Enter. We detect "fast typing" bursts to avoid
  // interfering with normal manual input in yantra fields.
  const latestHandleBarcodeSubmitRef = useRef(handleBarcodeSubmit);
  useEffect(() => {
    latestHandleBarcodeSubmitRef.current = handleBarcodeSubmit;
  }, [handleBarcodeSubmit]);
  useEffect(() => {
    let buffer = "";
    let lastTs = 0;
    let startedTs = 0;

    const isEditableTarget = (t) => {
      const el = t;
      if (!el) return false;
      const tag = String(el.tagName || "").toLowerCase();
      return (
        tag === "input" || tag === "textarea" || el.isContentEditable === true
      );
    };

    const onKeyDown = (e) => {
      // Let the existing shortcut handler handle scan result modal close.
      if (scanResultModalOpenRef.current) return;

      // Ignore function keys / modifiers.
      if (e.ctrlKey || e.altKey || e.metaKey) return;
      if (typeof e.key !== "string") return;
      if (e.key.startsWith("F")) return;

      const now = Date.now();
      const gap = now - lastTs;

      // Reset if user is typing slowly.
      if (gap > 80) {
        buffer = "";
        startedTs = now;
      }
      lastTs = now;

      if (e.key === "Enter") {
        const code = String(buffer || "").trim();
        buffer = "";

        // Only treat as scan if fast + looks like barcode.
        const elapsed = now - (startedTs || now);
        const isFast = elapsed > 0 && elapsed < 1200;
        const looksLikeBarcode = /^[0-9A-Za-z_-]{8,72}$/.test(code);

        if (isFast && looksLikeBarcode) {
          setBarcodeInput(code);
          latestHandleBarcodeSubmitRef.current?.(code, { clearInput: false });
          e.preventDefault();
          // Stop second submit from barcode <input> onKeyDown(Enter) for the same scan.
          e.stopPropagation();
        }
        return;
      }

      // Only record printable characters used by our barcode format.
      if (/^[0-9A-Za-z_-]$/.test(e.key)) {
        buffer += e.key;
        return;
      }

      // Any other key breaks scan burst.
      if (!isEditableTarget(e.target)) {
        buffer = "";
      }
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, []);

  // Game type/tab (persist across navigation via localStorage)
  const [activeTab, setActiveTab] = useState(getStoredActiveGameTab);
  const activeTabRef = useRef(activeTab);
  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);
  useEffect(() => {
    setStoredActiveGameTab(activeTab);
  }, [activeTab]);

  // Socket + session (useRef to avoid re-render loops; single socket instance)
  const sessionIdRef = useRef(null);
  const sessionDbIdRef = useRef(null);
  const profileRef = useRef(null);
  const [sessionId, setSessionId] = useState(null); // session_uid
  const [sessionNo, setSessionNo] = useState(null); // session_no
  const [sessionDbId, setSessionDbId] = useState(null); // numeric id
  useEffect(() => {
    sessionIdRef.current = sessionId;
    sessionDbIdRef.current = sessionDbId;
    profileRef.current = profile;
  }, [sessionId, sessionDbId, profile]);
  const [sessionStatus, setSessionStatus] = useState("live");
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  /** Right slideshow: last-draw winner (10s on new session + short countdown preview). */
  const [slideshowSpotlight, setSlideshowSpotlight] = useState(null);

  const [placeBetList, setPlaceBetList] = useState([]);
  const [placeBetListOpen, setPlaceBetListOpen] = useState(false);
  const [placeBetListLoading, setPlaceBetListLoading] = useState(false);
  const [rePrintLoading, setRePrintLoading] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [cancelTargetBet, setCancelTargetBet] = useState(null);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [selectedPlaceBet, setSelectedPlaceBet] = useState(null);
  const [printViewOpen, setPrintViewOpen] = useState(false);
  const [winPrintOpen, setWinPrintOpen] = useState(false);
  const [winPrintData, setWinPrintData] = useState(null);
  const handlePrint = () => {
    window.print();
  };
  /** Shorten placebet_uid e.g. YT_PLACEBET_1772022608767_PH4I → 8767_PH4I */
  const shortenPlaceBetUid = (uid) => {
    if (!uid || typeof uid !== "string") return uid || "—";
    const parts = uid.split("_");
    if (parts.length >= 2) {
      const last = parts[parts.length - 1];
      const prev = parts[parts.length - 2];
      const shortPrev = prev.length >= 4 ? prev.slice(-4) : prev;
      return `${shortPrev}_${last}`;
    }
    return uid;
  };
  /** Remove YT_PLACEBET_ prefix for display/print e.g. YT_PLACEBET_1773031729812_4KKR → 1773031729812_4KKR */
  const stripReceiptPrefix = (val) => {
    if (val == null || val === "") return "—";
    const s = String(val).trim();
    return s.replace(/^YT_PLACEBET_/i, "") || s || "—";
  };

  const formatSlipDateTime = (val) => {
    if (!val) return "—";
    const fmt = "DD-MM-YYYY hh:mm:ss A";
    if (typeof val === "number") return moment(val).format(fmt);
    const s = String(val);
    // DB Date serialized by JSON may look like: 2026-03-03T11:28:20.000Z (UTC)
    // moment() converts it to local time, causing +05:30 difference. Keep same time by formatting in UTC.
    if (s.includes("T") || s.endsWith("Z")) return moment.utc(s).format(fmt);
    return moment(s).format(fmt);
  };
  const [gameStoppedByAdmin, setGameStoppedByAdmin] = useState(false);

  // Countdown + time
  const endTimeRef = useRef(null);
  const endTimeStrRef = useRef(null);
  const startTimeStrRef = useRef(null);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [currentTimeDisplay, setCurrentTimeDisplay] = useState("");
  const timerRef = useRef(null);
  const lastFetchTodayLastDrawAtRef = useRef(0);
  const FETCH_LAST_DRAW_THROTTLE_MS = 8000; // Min 8s between API calls to prevent polling spam
  const sessionStatusRef = useRef(sessionStatus);
  const liveSessionInFlightRef = useRef(false);
  const prevSessionDbIdForLastDrawRef = useRef(null);
  /** When socket sends countdown, avoid local interval overwriting (stops 1s vs 2s fighting). */
  const lastSocketCountdownAtRef = useRef(0);
  const slideshowSpotlightSourceRef = useRef(null);
  const sessionSpotlightTimerRef = useRef(null);
  const sessionSpotlightDoneRef = useRef(new Set());
  const inCountdownWinnerWindowRef = useRef(false);

  const scrollContainerRef = useRef(null);
  const mainContentRef = useRef(null);
  const footerRef = useRef(null);
  const [isFooterOnlyVisible, setIsFooterOnlyVisible] = useState(false);

  useEffect(() => {
    const scrollEl = scrollContainerRef.current;
    const mainEl = mainContentRef.current;
    const footerEl = footerRef.current;
    if (!scrollEl || !mainEl || !footerEl) return;

    const visibilityRef = { main: true, footer: false };
    const updateFooterOnly = () => {
      setIsFooterOnlyVisible(!visibilityRef.main && visibilityRef.footer);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const target = entry.target;
          const ratio = entry.intersectionRatio;
          const visible = ratio > 0;
          if (target === mainEl) visibilityRef.main = visible;
          if (target === footerEl) visibilityRef.footer = visible;
        });
        updateFooterOnly();
      },
      { root: scrollEl, threshold: [0, 0.01, 1] },
    );

    observer.observe(mainEl);
    observer.observe(footerEl);
    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    sessionStatusRef.current = sessionStatus;
  }, [sessionStatus]);

  const TIME_DISPLAY_FORMAT = "h:mm A";

  // Keep body scroll disabled for this page only (main page never scrolls)
  useEffect(() => {
    document.body.classList.add("outlet-live-session-body");
    return () => {
      document.body.classList.remove("outlet-live-session-body");
    };
  }, []);

  // Toggle page scroll when history is open
  useEffect(() => {
    if (isHistoryOpen) {
      document.body.classList.remove("outlet-live-session-body");
    } else {
      document.body.classList.add("outlet-live-session-body");
    }
  }, [isHistoryOpen]);
  const formikRef = useRef(null);

  const fetchCurrentSessionPlaceBets = async () => {
    const sid = sessionDbIdRef.current;
    if (!sid) {
      outletToast.error("No active session");
      return;
    }
    setPlaceBetListLoading(true);
    setPlaceBetListOpen(true);
    try {
      const res = await liveSSApi.getCurrentSessionPlaceBets(sid);
      if (res?.isSuccess && Array.isArray(res?.data)) {
        setPlaceBetList(res.data);
      } else {
        setPlaceBetList([]);
      }
    } catch (err) {
      console.error("Error fetching place bets:", err);
      outletToast.error("Failed to load place bet list");
      setPlaceBetList([]);
    } finally {
      setPlaceBetListLoading(false);
    }
  };

  const rePrintLastPlaceBet = async () => {
    const sid = sessionDbIdRef.current;
    if (!sid) {
      outletToast.error("No active session");
      return;
    }
    if (rePrintLoading) return;
    setRePrintLoading(true);
    try {
      const res = await liveSSApi.getCurrentSessionPlaceBets(sid);
      const rows = res?.isSuccess && Array.isArray(res?.data) ? res.data : [];
      if (!rows.length) {
        outletToast.error("No place bet found to re-print");
        return;
      }
      const lastBet = rows[rows.length - 1];
      openPrintViewWithPreloadedBarcode(lastBet, false);
    } catch (err) {
      console.error("Error re-printing last bet:", err);
      outletToast.error("Failed to re-print last place bet");
    } finally {
      setRePrintLoading(false);
    }
  };

  // Keyboard shortcuts F3 / F5 / F4 / F11
  useEffect(() => {
    const handler = (e) => {
      // If scan result modal is open, any key closes it and blocks shortcuts.
      if (scanResultModalOpenRef.current) {
        e.preventDefault();
        closeScanResultModal();
        return;
      }
      if (e.key === "F3") {
        e.preventDefault();
        if (activeTabRef.current !== "3min") setActiveTab("3min");
      }
      if (e.key === "F5") {
        e.preventDefault();
        if (activeTabRef.current !== "5min") setActiveTab("5min");
      }
      if (e.key === "F4") {
        e.preventDefault();
        fetchCurrentSessionPlaceBets();
      }
      if (e.key === "F11") {
        e.preventDefault();
        handleBack();
      }

      if (e.key === "F6") {
        e.preventDefault();
        (formikRef.current?.values?.yantra_list || []).forEach((_, i) =>
          formikRef.current?.setFieldValue(
            `yantra_list[${i}].yantra_count`,
            null,
          ),
        );
        setTimeout(() => {
          focusFirstYantraInput();
        }, 0);
      }
      if (e.key === "F7") {
        e.preventDefault();
        rePrintLastPlaceBet();
      }
      // 🔥 ENTER = PLACE BET
      if (e.key === "Enter") {
        const btn = document.querySelector(".place-btn");
        if (sessionStatusRef.current !== "RUNNING" && btn && !btn.disabled) {
          e.preventDefault();
          btn.click();
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const parseEndTime = (timeVal) => {
    if (timeVal == null || timeVal === "") return null;
    if (timeVal instanceof Date) {
      const m = moment.utc(timeVal);
      return m.isValid() ? { endM: m, isUTC: true } : null;
    }
    const s = String(timeVal).trim();
    if (!s) return null;
    const hasUtcMarker = s.endsWith("Z") || s.includes("T");
    // MySQL DATETIME in JSON is often "YYYY-MM-DD HH:mm:ss" with no TZ; server stores UTC.
    const mysqlNaiveUtc =
      !hasUtcMarker && /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(s);
    const m = hasUtcMarker
      ? moment.utc(s)
      : mysqlNaiveUtc
        ? moment.utc(s.replace(" ", "T"))
        : moment(s);
    return m.isValid()
      ? { endM: m, isUTC: hasUtcMarker || mysqlNaiveUtc }
      : null;
  };

  /**
   * Same instant rules as draw-time display; optional start clamps so remaining never exceeds session span.
   */
  const getRemainingSecondsFromEndTime = (endTimeStr, opts = {}) => {
    if (endTimeStr == null || endTimeStr === "") return 0;
    const parsed = parseEndTime(endTimeStr);
    if (!parsed?.endM?.isValid()) return 0;

    let secs = parsed.endM.diff(moment(), "seconds", true);
    if (!Number.isFinite(secs)) return 0;
    secs = Math.max(0, Math.floor(secs));

    const { startTimeStr } = opts;
    if (startTimeStr != null && startTimeStr !== "") {
      const sp = parseEndTime(startTimeStr);
      if (sp?.endM?.isValid()) {
        const span = Math.floor(parsed.endM.diff(sp.endM, "seconds", true));
        if (span > 0) secs = Math.min(secs, span);
      }
    }

    return secs;
  };

  const [loading, setLoading] = useState(false);
  const [yantraLoading, setYantraLoading] = useState(false);

  const [initialValues, setInitialValues] = useState({
    wallet_balance: 0,
    paid_amount: 0,
    winning_amount: 0,
    last_winning_amount: 0,
    total_bet: 0,
    amount: 0,
    date: "",
    draw_time: "",
    remainning_time: 0,
    current_time: "",
    session_id: "",
    session_uid: "",
    session_status: "",
    game_type: "",
    start_time: "",
    end_time: "",
    percentage: "",
    winner_type: "",
    winner_count: 0,
    session_no: "",
  });

  // Fetch today's last draw list for the active game type (throttled to prevent polling spam)
  const fetchTodayLastDraw = async (gameType = activeTab, opts = {}) => {
    const { force = false } = opts;
    const now = Date.now();
    if (
      !force &&
      now - lastFetchTodayLastDrawAtRef.current < FETCH_LAST_DRAW_THROTTLE_MS
    ) {
      return;
    }
    lastFetchTodayLastDrawAtRef.current = now;
    try {
      setHistoryLoading(true);
      const response = await liveSSApi.getTodayLastDraw(gameType, opts);
      if (opts.signal?.aborted) return;
      if (response?.isSuccess && Array.isArray(response?.data)) {
        setHistory(response.data);
      } else {
        setHistory([]);
      }
    } catch (error) {
      if (
        opts.signal?.aborted ||
        error?.name === "AbortError" ||
        error?.code === "ERR_CANCELED"
      )
        return;
      console.error("Error in fetchTodayLastDraw (outlet):", error);
      setHistory([]);
    } finally {
      if (!opts.signal?.aborted) setHistoryLoading(false);
    }
  };

  const openPrintView = (bet) => {
    setSelectedPlaceBet(bet);
    setPrintViewOpen(true);
  };

  const openCancelConfirm = (bet) => {
    setCancelTargetBet(bet);
    setCancelConfirmOpen(true);
  };

  const closeCancelConfirm = () => {
    setCancelConfirmOpen(false);
    setCancelTargetBet(null);
    setCancelSubmitting(false);
    setTimeout(() => {
      focusFirstYantraInput();
    }, 0);
  };

  const confirmCancelPlaceBet = async () => {
    const bet = cancelTargetBet;
    if (!bet?.id) return;
    if (cancelSubmitting) return;
    setCancelSubmitting(true);
    try {
      const res = await liveSSApi.cancelPlaceBet(bet.id);
      if (res?.isSuccess) {
        outletToast.success(res?.message || "Bet cancelled");
        // remove from list
        setPlaceBetList((prev) => (prev || []).filter((b) => b.id !== bet.id));
        // update wallet balance if provided
        const wb = res?.walletData?.wallet_balance;
        if (wb != null && !Number.isNaN(Number(wb))) {
          const newWallet = Number(wb);
          setInitialValues((p) => ({ ...p, wallet_balance: newWallet }));
          if (formikRef.current?.setFieldValue) {
            formikRef.current.setFieldValue("wallet_balance", newWallet);
          }
        }
        closeCancelConfirm();
      } else {
        outletToast.error(res?.message || "Failed to cancel bet");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.message || "Failed to cancel bet";
      outletToast.error(msg);
    } finally {
      setCancelSubmitting(false);
    }
  };

  const openWinPrintViewWithAutoPrint = (payload, autoPrint = true) => {
    setWinPrintData(payload);
    setWinPrintOpen(true);
    if (!autoPrint) return;
    setTimeout(() => {
      window.print();
      setTimeout(() => {
        setWinPrintOpen(false);
        setWinPrintData(null);
      }, 350);
    }, 350);
  };

  /** Preload barcode image, then open print view (and optionally auto-print). Fixes barcode not showing in modal until Print is clicked. */
  const openPrintViewWithPreloadedBarcode = (bet, autoPrint = false) => {
    const barcodeValue = bet?.bar_code || bet?.placebet_uid;
    if (!barcodeValue) {
      openPrintView(bet);
      if (autoPrint) {
        setTimeout(() => {
          window.print();
          // After a short delay, auto-close the receipt for kiosk flow.
          setTimeout(() => {
            setPrintViewOpen(false);
            setSelectedPlaceBet(null);
          }, 350);
        }, 350);
      }
      return;
    }
    const barcodeUrl = `https://barcode.tec-it.com/barcode.ashx?data=${encodeURIComponent(barcodeValue)}&code=Code128`;
    const img = new Image();
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      openPrintView(bet);
      if (autoPrint) {
        setTimeout(() => {
          window.print();
          setTimeout(() => {
            setPrintViewOpen(false);
            setSelectedPlaceBet(null);
          }, 350);
        }, 350);
      }
    };
    img.onload = finish;
    img.onerror = finish;
    img.src = barcodeUrl;
    setTimeout(finish, 2500);
  };

  async function getLiveSessionData(gameType = activeTab, opts = {}) {
    const { silent = false, keepCurrentOnEmpty = false, signal } = opts;

    if (liveSessionInFlightRef.current) return;
    liveSessionInFlightRef.current = true;
    setLoading(true);
    liveSSApi
      .getLiveSessionData(gameType, { signal })
      .then((response) => {
        if (response.isSuccess && response?.data) {
          const data = response?.data;
          const walletData = response?.walletData;
          const walletBalance = walletData?.wallet_balance ?? 0;

          const paidAmount =
            walletData?.paid_amount ?? walletData?.paid_amoun ?? 0;
          const parsedEnd = parseEndTime(data?.end_time);
          endTimeRef.current = parsedEnd;
          endTimeStrRef.current = data?.end_time || null;
          startTimeStrRef.current = data?.start_time || null;

          setSessionId(data.session_uid);
          setSessionNo(data.session_no);
          setSessionDbId(data.id);
          setSessionStatus(data.status || "live");

          const start = moment.utc(data?.start_time);
          const endParsed = parsedEnd;
          const endM = endParsed ? endParsed.endM : moment.utc(data?.end_time);
          const date = start.isValid()
            ? start.local().format("DD-MM-YYYY")
            : moment().format("DD-MM-YYYY");
          const drawTime = endM.isValid()
            ? endM.clone().local().format(TIME_DISPLAY_FORMAT)
            : "";
          const currentTime = moment().local().format(TIME_DISPLAY_FORMAT);
          const remaining = getRemainingSecondsFromEndTime(data?.end_time, {
            startTimeStr: data?.start_time,
          });
          lastSocketCountdownAtRef.current = Date.now();
          setRemainingSeconds(remaining);
          setCurrentTimeDisplay(currentTime);

          const nextValues = {
            wallet_balance: walletBalance,
            paid_amount: paidAmount,
            winning_amount: walletData?.winning_amount ?? 0,
            last_winning_amount: response?.lastWinningAmount ?? 0,
            date,
            draw_time: drawTime,
            current_time: currentTime,
            remainning_time: remaining,
            session_id: data?.id || "",
            session_uid: data?.session_uid || "",
            session_status: data?.status || "",
            game_type: data?.game_type || "",
            start_time: data?.start_time || "",
            end_time: data?.end_time || "",
            percentage: data?.percentage || "",
            winner_type: data?.winner_type || "",
            winner_count: data?.winner_count || 0,
            session_no: data?.session_no || "",
          };
          setInitialValues((prev) => ({ ...prev, ...nextValues }));
          if (formikRef.current?.setValues) {
            formikRef.current.setValues((prev) => ({ ...prev, ...nextValues }));
          }
          setGameStoppedByAdmin(false);
        } else {
          if (!keepCurrentOnEmpty) {
            endTimeStrRef.current = null;
            startTimeStrRef.current = null;
            endTimeRef.current = null;
            setSessionId(null);
            setCurrentTimeDisplay("");
          }
          setRemainingSeconds(0);
          const stopped =
            response?.gameStopped ||
            (response?.message &&
              String(response.message).includes("stopped by admin"));
          setGameStoppedByAdmin(!!stopped);
          if (!silent)
            outletToast.error(response?.message || "No running session");
        }
      })
      .catch((error) => {
        if (
          signal?.aborted ||
          error?.name === "AbortError" ||
          error?.code === "ERR_CANCELED"
        )
          return;
        console.error("Error get getLiveSessionData:", error);
        if (!silent && !keepCurrentOnEmpty) {
          endTimeStrRef.current = null;
          startTimeStrRef.current = null;
          endTimeRef.current = null;
          setSessionId(null);
          setRemainingSeconds(0);
          setCurrentTimeDisplay("");
        } else if (!silent) {
          setRemainingSeconds(0);
        }
        setGameStoppedByAdmin(false);
        if (error?.response?.data?.userActiveStatus === 0) {
          outletToast.error(
            error?.response?.data?.message || "User is not active",
          );
          logout();
        } else if (!silent && error?.response?.data?.message) {
          outletToast.error(error?.response?.data?.message);
        } else {
          outletToast.error("Error in getting game sessions");
        }
      })
      .finally(() => {
        liveSessionInFlightRef.current = false;
        setLoading(false);
      });
  }

  const getYantraData = async (opts = {}) => {
    setYantraLoading(true);
    liveSSApi
      .getYantraListData(opts)
      .then((response) => {
        if (response.isSuccess) {
          const yantras = response?.data || [];

          const mappedYantras = yantras.map((y) => ({
            yantra_id: y.id,
            yantra_name: y.yantra_name,
            yantra_img: y.yantra_img,
            yantra_amount: y.amount,
            yantra_count: null,
          }));

          setInitialValues((prev) => ({
            ...prev,
            yantra_list: mappedYantras,
          }));
        } else {
          outletToast.error(response.message);
        }
      })
      .catch((error) => {
        if (
          opts.signal?.aborted ||
          error?.name === "AbortError" ||
          error?.code === "ERR_CANCELED"
        )
          return;
        console.error("Error get getYantraListData:", error);
        outletToast.error("Error get yantra list data");
      })
      .finally(() => {
        setYantraLoading(false);
      });
  };

  // Initial load + game type change (cancel previous requests only on tab change, not first load)
  const initialLoadDoneRef = useRef(false);
  useEffect(() => {
    const isFirstLoad = !initialLoadDoneRef.current;
    if (isFirstLoad) initialLoadDoneRef.current = true;
    const ac = new AbortController();
    const signal = isFirstLoad ? undefined : ac.signal;
    getLiveSessionData(activeTab, { signal });
    getYantraData({ signal });
    fetchTodayLastDraw(activeTab, { signal, force: true });
    settingApi
      .getSettingData({ signal })
      .then((res) => {
        if (signal?.aborted) return;
        if (res?.isSuccess && res?.data) {
          setSettingData(res.data);
          setLiveSessionNote1(res.data.live_session_note1 ?? "");
          setLiveSessionNote2(res.data.live_session_note2 ?? "");
          setWinningSlipNote(res.data.winning_slip_note ?? "");
        }
      })
      .catch((err) => {
        if (
          signal?.aborted ||
          err?.name === "AbortError" ||
          err?.code === "ERR_CANCELED"
        )
          return;
      });
    return () => {
      if (!isFirstLoad) ac.abort();
    };
  }, [activeTab]);

  // Single socket instance: game status broadcast + session join (prevents reconnect on every state change)
  const sessionSocketRef = useRef(null);
  const lastJoinedSessionIdRef = useRef(null);

  useEffect(() => {
    if (sessionSocketRef.current) return;
    const s = io(process.env.REACT_APP_NODE_URL, {
      transports: ["websocket"],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
    });
    sessionSocketRef.current = s;

    s.on("connect", () => {
      console.log("Socket connected:", s.id);
      const sid = sessionIdRef.current;
      const sdbId = sessionDbIdRef.current;
      const prof = profileRef.current;
      if (sid && sdbId && prof?.user_id && prof?.name) {
        const payload = {
          sessionId: sid,
          sessionDbId: sdbId,
          userId: prof.user_id,
          username: prof.name,
          role: prof.role,
          gameType: activeTabRef.current,
        };
        s.emit("joinSession", payload);
        lastJoinedSessionIdRef.current = sid;
      }
    });

    s.on("disconnect", () => {
      console.log("Socket disconnected");
      lastSocketCountdownAtRef.current = 0;
    });

    // s.on("countdown", ({ remaining, currentTime }) => {
    //   if (typeof remaining === "number") setRemainingSeconds(remaining);
    //   if (currentTime) {
    //     const formatted = moment
    //       .utc(currentTime)
    //       .local()
    //       .format(TIME_DISPLAY_FORMAT);
    //     setCurrentTimeDisplay(formatted);
    //   }
    // });

    s.on("gameResult", ({ winnerYantra, winnerYantraIds }) => {
      getLiveSessionData(activeTabRef.current, { silent: true });
    });

    s.on("gameStatusChange", () => {
      getLiveSessionData(activeTabRef.current, { silent: true });
    });

    s.on("newRound", () => {
      getLiveSessionData(activeTabRef.current, { silent: true });
    });

    // DB-backed new session notification (broadcasted server-side)
    s.on("dbNewSession", ({ gameType }) => {
      if (!gameType || gameType !== activeTabRef.current) return;
      getLiveSessionData(activeTabRef.current, {
        silent: true,
        keepCurrentOnEmpty: true,
      });
    });

    s.on("sessionEnded", () => {
      getLiveSessionData(activeTabRef.current, { silent: true });
    });

    return () => {
      s.disconnect();
      sessionSocketRef.current = null;
      lastJoinedSessionIdRef.current = null;
    };
  }, []);

  // Join session when sessionId/sessionDbId change (e.g. tab switch); leave old session first
  // Initial join and reconnect are handled by the connect handler in the socket setup above
  useEffect(() => {
    const socket = sessionSocketRef.current;
    if (
      !socket ||
      !sessionId ||
      !sessionDbId ||
      !profile?.user_id ||
      !profile?.name
    )
      return;
    if (!socket.connected) return; // connect handler will join when socket connects

    const prevSessionId = lastJoinedSessionIdRef.current;
    if (prevSessionId === sessionId) return; // already joined this session (e.g. reconnect)

    if (prevSessionId) {
      socket.emit("leaveSession", { sessionId: prevSessionId });
    }
    const payload = {
      sessionId,
      sessionDbId,
      userId: profile.user_id,
      username: profile.name,
      role: profile.role,
      gameType: activeTabRef.current,
    };
    console.log("Joining session:", sessionId);
    socket.emit("joinSession", payload);
    lastJoinedSessionIdRef.current = sessionId;
  }, [sessionId, sessionDbId, profile?.user_id, profile?.name, profile?.role]);

  // Last draw: refresh on first load / tab (see activeTab effect) and only when DB session id changes,
  // so the strip does not jump ahead while the current round is still running.
  useEffect(() => {
    if (!sessionDbId) {
      prevSessionDbIdForLastDrawRef.current = null;
      return;
    }
    const prev = prevSessionDbIdForLastDrawRef.current;
    prevSessionDbIdForLastDrawRef.current = sessionDbId;
    if (prev != null && prev !== sessionDbId) {
      fetchTodayLastDraw(activeTabRef.current, { force: true });
    }
  }, [sessionDbId]);

  // Fallback countdown only if socket countdown is stale (disconnect / missed ticks).
  // Otherwise local tick every 2s fights server every 1s → visible jumps (e.g. 45 → 44 → 46).
  useEffect(() => {
    if (!sessionId) return;
    lastSocketCountdownAtRef.current = 0;

    const SOCKET_COUNTDOWN_TTL_MS = 3500;

    const tick = () => {
      const socketFresh =
        Date.now() - lastSocketCountdownAtRef.current < SOCKET_COUNTDOWN_TTL_MS;
      if (socketFresh) return;

      const endTimeStr = endTimeStrRef.current;
      const secs = getRemainingSecondsFromEndTime(endTimeStr, {
        startTimeStr: startTimeStrRef.current,
      });
      setRemainingSeconds(secs);
      setCurrentTimeDisplay(moment().format(TIME_DISPLAY_FORMAT));
    };

    tick();
    const fallbackInterval = setInterval(tick, 1000);

    return () => clearInterval(fallbackInterval);
  }, [sessionId]);

  useEffect(() => {
    return () => {
      clearTimeout(sessionSpotlightTimerRef.current);
    };
  }, []);

  // New RUNNING session: show last completed draw's winner in the slideshow for 10s (once per session id).
  useEffect(() => {
    if (!sessionDbId || String(sessionStatus).toUpperCase() !== "RUNNING")
      return;
    if (sessionSpotlightDoneRef.current.has(sessionDbId)) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await liveSSApi.getTodayLastDraw(activeTab);
        if (cancelled) return;
        if (!res?.isSuccess || !Array.isArray(res.data) || !res.data[0]) return;
        const w = getFirstWinnerFromHistoryItem(res.data[0]);
        if (!w) return;
        sessionSpotlightDoneRef.current.add(sessionDbId);
        clearTimeout(sessionSpotlightTimerRef.current);
        if (slideshowSpotlightSourceRef.current === "countdown") return;
        slideshowSpotlightSourceRef.current = "session";
        setSlideshowSpotlight({ ...w });
        sessionSpotlightTimerRef.current = setTimeout(() => {
          if (slideshowSpotlightSourceRef.current === "session") {
            slideshowSpotlightSourceRef.current = null;
            setSlideshowSpotlight(null);
          }
        }, 10000);
      } catch (_) {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionDbId, activeTab, sessionStatus]); // eslint-disable-line react-hooks/exhaustive-deps -- liveSSApi

  // 3 min: remaining in 2:45–2:50 (165–170s). 5 min: 4:45–4:50 (285–290s). Re-show last winner with effect.
  useEffect(() => {
    const st = String(sessionStatus || "").toUpperCase();
    const is3 = activeTab === "3min";
    const is5 = activeTab === "5min";
    const inWindow =
      st === "RUNNING" &&
      ((is3 && remainingSeconds >= 165 && remainingSeconds <= 170) ||
        (is5 && remainingSeconds >= 285 && remainingSeconds <= 290));

    if (inWindow && !inCountdownWinnerWindowRef.current) {
      inCountdownWinnerWindowRef.current = true;
      const w = getFirstWinnerFromHistoryItem(history[0]);
      if (w) {
        clearTimeout(sessionSpotlightTimerRef.current);
        slideshowSpotlightSourceRef.current = "countdown";
        setSlideshowSpotlight({ ...w });
      }
    } else if (!inWindow && inCountdownWinnerWindowRef.current) {
      inCountdownWinnerWindowRef.current = false;
      if (slideshowSpotlightSourceRef.current === "countdown") {
        slideshowSpotlightSourceRef.current = null;
        setSlideshowSpotlight(null);
      }
    }
  }, [remainingSeconds, activeTab, sessionStatus, history]);

  const formatRemaining = (seconds) => {
    if (seconds == null || isNaN(seconds) || seconds < 0) return "00:00";
    const sec = Math.floor(Number(seconds));
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };

  const handleBack = () => {
    routerHistory.push("/dashboard");
  };

  const containerRef = useRef(null);

  const createFirework = () => {
    const container = containerRef.current;
    if (!container) return;

    const colors = [
      "#FFD700",
      "#FF3CAC",
      "#00F5FF",
      "#FF5733",
      "#7CFF00",
      "#FF6B9D",
      "#C084FC",
      "#FBBF24",
      "#34D399",
      "#60A5FA",
      "#F472B6",
      "#A78BFA",
      "#FACC15",
    ];

    const pickColor = () => colors[Math.floor(Math.random() * colors.length)];

    const rect = container.getBoundingClientRect();
    const w = rect.width;
    const h = rect.height;
    const x = Math.random() * w;
    const y = Math.random() * h * 0.7;

    // 25 + 13 = 38 particles per burst (~8–15 extra for denser edge sparkle)
    const particlesPerBurst = 38;
    for (let i = 0; i < particlesPerBurst; i++) {
      const particle = document.createElement("div");
      particle.className = "particle";

      const angle = Math.random() * 2 * Math.PI;
      const distance = Math.random() * 80 + 40;

      const dx = Math.cos(angle) * distance + "px";
      const dy = Math.sin(angle) * distance + "px";

      particle.style.setProperty("--x", dx);
      particle.style.setProperty("--y", dy);
      particle.style.left = x + "px";
      particle.style.top = y + "px";

      const color = pickColor();
      particle.style.background = color;
      particle.style.boxShadow = `0 0 10px ${color}`;

      container.appendChild(particle);

      setTimeout(() => {
        particle.remove();
      }, 1000);
    }

    // Twinkling sparkles (second animation type)
    const sparkleCount = 10;
    for (let s = 0; s < sparkleCount; s++) {
      const sp = document.createElement("div");
      sp.className = "yantra-fw-sparkle";
      sp.setAttribute("aria-hidden", "true");
      sp.textContent = "✦";
      const pad = 0.06;
      sp.style.left = `${pad * w + Math.random() * w * (1 - 2 * pad)}px`;
      sp.style.top = `${pad * h + Math.random() * h * (1 - 2 * pad)}px`;
      const c = pickColor();
      sp.style.color = c;
      sp.style.textShadow = `0 0 8px ${c}, 0 0 14px ${c}`;
      sp.style.animationDelay = `${Math.random() * 0.35}s`;
      container.appendChild(sp);
      setTimeout(() => sp.remove(), 1300);
    }

    // Rising embers (third animation type — drift up from lower area)
    const emberCount = 9;
    for (let e = 0; e < emberCount; e++) {
      const em = document.createElement("div");
      em.className = "yantra-fw-ember";
      em.setAttribute("aria-hidden", "true");
      em.style.left = `${Math.random() * w}px`;
      em.style.top = `${h * (0.55 + Math.random() * 0.38)}px`;
      const drift = (Math.random() - 0.5) * 70;
      const rise = -(70 + Math.random() * 110);
      em.style.setProperty("--ember-drift", `${drift}px`);
      em.style.setProperty("--ember-rise", `${rise}px`);
      const c = pickColor();
      em.style.background = c;
      em.style.boxShadow = `0 0 10px ${c}`;
      em.style.animationDelay = `${Math.random() * 0.25}s`;
      container.appendChild(em);
      setTimeout(() => em.remove(), 2000);
    }
  };

  useEffect(() => {
    if (!slideshowSpotlight) return;
    const kick = setTimeout(() => createFirework(), 80);
    const interval = setInterval(createFirework, 900);
    return () => {
      clearTimeout(kick);
      clearInterval(interval);
    };
  }, [slideshowSpotlight]);

  return (
    <div className="outlet-live-root">
      <header className="outlet-live-header">
        <div className="outlet-header-left">
          <AppLogo
            showSubtext={true}
            size="medium"
            title={settingData?.main_title}
            subTitle={settingData?.sub_title}
          />
        </div>
        <div className="outlet-header-center">
          <div className="outlet-marquee-wrap">
            <marquee direction="left" className="outlet-live-note">
              <span className="outlet-disclaimer-note">નોંધ:-</span>
              {liveSessionNote1 || "યંત્રની ખરીદી 24 કલાક માન્ય રહેશે."} Program
              SN:#{sessionNo ?? "—"} (#{sessionId ?? "—"})
            </marquee>
          </div>
        </div>
        <div className="outlet-header-right">
          <button
            type="button"
            className="outlet-header-wallet-date"
            onClick={() => setShowWalletBL(!showWalletBL)}
          >
            <span className="outlet-header-userid-box">
              {user.username
                ? user.username
                : user.user_username
                  ? user.user_username
                  : user.game_id
                    ? user.game_id
                    : "—"}
            </span>
            <span className="outlet-header-wallet">
              {showWalletBL ? (
                <>
                  Wallet: ₹
                  {Number(
                    (currentFormValuesRef.current &&
                      currentFormValuesRef.current.wallet_balance) ??
                      initialValues.wallet_balance ??
                      0,
                  ).toFixed(2)}
                </>
              ) : (
                <>Wallet: ---</>
              )}
            </span>
          </button>
        </div>
      </header>

      <div ref={scrollContainerRef} className="outlet-live-scroll-container">
        {gameStoppedByAdmin && (
          <div className="outlet-game-stopped-banner" role="alert">
            Purchase stopped by admin. Please try again later.
          </div>
        )}
        <div ref={mainContentRef} className="outlet-live-main">
          <Formik
            key={`session-${sessionDbId ?? sessionId ?? "none"}`}
            innerRef={formikRef}
            enableReinitialize
            initialValues={initialValues}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={async (
              values,
              { setSubmitting, resetForm, setFieldValue },
            ) => {
              setSubmitting(true);

              if (
                !isFooterOnlyVisible &&
                remainingSeconds > 0 &&
                remainingSeconds <= 10
              ) {
                outletToast.error(
                  "Purchasing is closed in the last 10 seconds.",
                );
                setSubmitting(false);
                return;
              }
              const wallet = Number(values.wallet_balance || 0);
              const totalBetSubmit = Number(values.total_bet || 0);
              if (wallet < 1 && barcodeInput.length === 0) {
                outletToast.error("Insufficient wallet balance.");
                setSubmitting(false);
                return;
              }
              if (totalBetSubmit < 1 && barcodeInput.length === 0) {
                outletToast.error("Please place a bet.");
                setSubmitting(false);
                return;
              }

              if (barcodeInput.length === 0) {
                try {
                  const response = await liveSSApi.savePlaceBetData(values);
                  if (response.isSuccess) {
                    outletToast.success("Bet placed successfully");

                    // Auto-open receipt + auto-print (fallback to placebet_uid if bar_code missing)
                    const placebet_uid = response?.placebet_uid || null;
                    const bar_code = response?.bar_code || null;
                    const created_at = Date.now();
                    const betForPrint = {
                      ...values,
                      placebet_uid,
                      bar_code,
                      created_at,
                      user_id: profile?.user_id ?? values?.user_id,
                      user_name: profile?.name ?? values?.user_name,
                      user_username: profile?.username ?? values?.user_username,
                      yantra_list: Array.isArray(values?.yantra_list)
                        ? values.yantra_list
                            .filter((y) => Number(y?.yantra_count) > 0)
                            .map((y) => ({
                              ...y,
                              yantra_count:
                                y?.yantra_count == null ||
                                y?.yantra_count === ""
                                  ? 0
                                  : Number(y.yantra_count),
                            }))
                        : [],
                    };
                    openPrintViewWithPreloadedBarcode(betForPrint, true);

                    const newWalletBalanceRaw =
                      response?.walletData?.wallet_balance;
                    const newWalletBalance =
                      newWalletBalanceRaw != null &&
                      !Number.isNaN(Number(newWalletBalanceRaw))
                        ? Number(newWalletBalanceRaw)
                        : null;

                    const resetYantras = (values.yantra_list || []).map(
                      (y) => ({
                        ...y,
                        yantra_count: null,
                      }),
                    );

                    resetForm({
                      values: {
                        ...values,
                        wallet_balance:
                          newWalletBalance != null
                            ? newWalletBalance
                            : values.wallet_balance,
                        total_bet: 0,
                        amount: 0,
                        yantra_list: resetYantras,
                      },
                    });
                    // After resetting form, refocus first yantra input
                    setTimeout(() => {
                      focusFirstYantraInput();
                    }, 0);

                    if (newWalletBalance == null) {
                      try {
                        const refresh = await liveSSApi.getLiveSessionData(
                          activeTabRef.current,
                        );
                        const wb = refresh?.walletData?.wallet_balance;
                        const pa =
                          refresh?.walletData?.paid_amount ??
                          refresh?.walletData?.paid_amoun;

                        if (wb != null && !Number.isNaN(Number(wb))) {
                          setFieldValue("wallet_balance", Number(wb));
                        }
                        if (pa != null && !Number.isNaN(Number(pa))) {
                          setFieldValue("paid_amount", Number(pa));
                        }
                      } catch (_) {
                        // ignore refresh failure; bet already succeeded
                      }
                    }
                  } else {
                    if (response?.userActiveStatus === 0) {
                      outletToast.error("User is not active");
                      logout();
                    } else {
                      outletToast.error(
                        response?.message || "Failed to place bet",
                      );
                    }
                  }
                } catch (error) {
                  console.error("Error in savePlaceBetData:", error);
                  if (error?.response?.data?.userActiveStatus === 0) {
                    outletToast.error(
                      error?.response?.data?.message || "User is not active",
                    );
                    logout();
                  } else {
                    outletToast.error(
                      error?.response?.data?.message || "Error in placing bet",
                    );
                  }
                } finally {
                  setSubmitting(false);
                  setAllYantraInputUpdate("");
                }
              }
            }}
          >
            {({ values, isSubmitting, setFieldValue, handleSubmit }) => {
              currentFormValuesRef.current = values;
              const totalBet = Number(values.total_bet || 0);
              const showWinnerProcessingModal =
                !isFooterOnlyVisible &&
                remainingSeconds > 0 &&
                remainingSeconds <= 10;
              const isPlaceBetDisabled =
                isSubmitting ||
                remainingSeconds <= 0 ||
                remainingSeconds <= 10 ||
                values.session_status !== "RUNNING";

              return (
                <YantraImagesLoader
                  values={values}
                  templateVersion={templateImageVersion}
                >
                  {(yantraImages) => (
                    <Form className="outlet-live-form">
                      <SyncTotalBetAmount />

                      {showWinnerProcessingModal && (
                        <div
                          role="dialog"
                          aria-modal="true"
                          aria-label="Winner processing"
                          style={{
                            position: "fixed",
                            inset: 0,
                            background: "rgba(0, 0, 0, 0.55)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 9999,
                            pointerEvents: "all",
                          }}
                          onClick={(e) => e.preventDefault()}
                        >
                          <div
                            style={{
                              width: "min(360px, calc(100vw - 32px))",
                              background: "#fff",
                              borderRadius: 12,
                              padding: "22px 20px",
                              boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
                              textAlign: "center",
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div style={{ fontWeight: 700, fontSize: 18 }}>
                              Winner processing
                            </div>
                            <div style={{ marginTop: 6, color: "#555" }}>
                              Purchasing is closed in the last 10 seconds.
                            </div>
                            <div style={{ marginTop: 10, color: "#111" }}>
                              Next round starts soon ({remainingSeconds}s)
                            </div>
                            <div
                              style={{
                                marginTop: 14,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 10,
                              }}
                            >
                              <svg
                                width="22"
                                height="22"
                                viewBox="0 0 24 24"
                                aria-hidden="true"
                              >
                                <circle
                                  cx="12"
                                  cy="12"
                                  r="9"
                                  fill="none"
                                  stroke="#667eea"
                                  strokeWidth="3"
                                  strokeLinecap="round"
                                  strokeDasharray="42 56"
                                >
                                  <animateTransform
                                    attributeName="transform"
                                    type="rotate"
                                    from="0 12 12"
                                    to="360 12 12"
                                    dur="0.9s"
                                    repeatCount="indefinite"
                                  />
                                </circle>
                              </svg>
                              <span style={{ color: "#333" }}>
                                Please wait...
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="outlet-live-content">
                        {/* Left: yantra grid */}
                        <div className="outlet-left">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <div className="d-flex justify-content-start align-items-center">
                                <div className="ms-3 me-4">
                                  <div className="outlet-draw-game_type">
                                    {activeTab === "5min" ? "5" : "3"} Min
                                  </div>
                                </div>
                                <div className="me-4">
                                  <span className="outlet-remaining-time">
                                    {formatRemaining(remainingSeconds)}
                                  </span>
                                </div>
                                <div className="me-4">
                                  <span className="outlet-current-date">
                                    {initialValues.date ||
                                      moment().format("DD-MM-YYYY")}
                                  </span>
                                </div>
                                <div className="me-4">
                                  <span className="outlet-current-time">
                                    {currentTimeDisplay || "—"}
                                  </span>
                                </div>
                                <div className="me-4">
                                  <span className="outlet-draw-time">
                                    {values.draw_time || "—"}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div>
                              <div className="outlet-activity-row">
                                <span>Amount </span>
                                <span className="ms-2 outlet-amount-value">
                                  {Number(values.amount || 0).toFixed(2)}
                                </span>
                              </div>
                            </div>
                          </div>
                          {yantraLoading ? (
                            <div className="outlet-center-loader">
                              <Loader message="Loading yantras..." />
                            </div>
                          ) : (
                            <FieldArray name="yantra_list">
                              {() => (
                                <div className="outlet-grid-wrap">
                                  <div
                                    className="outlet-grid-inner"
                                    ref={yantraGridInnerRef}
                                  >
                                    <AutoFocusFirstYantraInput
                                      enabled={!yantraLoading}
                                      inputRef={firstYantraInputRef}
                                    />
                                    {(values.yantra_list || []).map(
                                      (yantra, idx) => {
                                        const imgSrc =
                                          yantraImages[idx]?.imgSrc ??
                                          getYantraImage(
                                            yantra.yantra_img,
                                            yantra.yantra_id,
                                          );
                                        // const count = Number(
                                        //   yantra.yantra_count || 0,
                                        // );

                                        // const handleCardClick = () => {
                                        //   const next = count + 1;
                                        //   setFieldValue(
                                        //     `yantra_list[${idx}].yantra_count`,
                                        //     String(next),
                                        //   );
                                        // };

                                        return (
                                          <div
                                            key={yantra.yantra_id || idx}
                                            className={`outlet-yantra-card outlet-yantra-card-variant-${idx % 12}`}
                                            // onClick={handleCardClick}
                                          >
                                            <div className="outlet-yantra-img-wrap">
                                              {imgSrc ? (
                                                <img
                                                  loading="lazy"
                                                  src={imgSrc}
                                                  alt={
                                                    yantra.yantra_name ||
                                                    "Yantra"
                                                  }
                                                />
                                              ) : (
                                                <div className="outlet-yantra-placeholder" />
                                              )}
                                            </div>
                                            <div className="outlet-yantra-name">
                                              {yantra.yantra_name || "Yantra"}
                                            </div>
                                            <div className="outlet-yantra-count">
                                              <Field
                                                name={`yantra_list[${idx}].yantra_count`}
                                              >
                                                {({ field, form }) => (
                                                  <input
                                                    {...field}
                                                    data-field-name={field.name}
                                                    ref={(el) => {
                                                      if (!el) return;
                                                      yantraInputRefs.current[
                                                        idx
                                                      ] = el;
                                                      if (idx === 0)
                                                        firstYantraInputRef.current =
                                                          el;
                                                    }}
                                                    type="text"
                                                    inputMode="numeric"
                                                    pattern="[0-9]*"
                                                    value={
                                                      field.value === null ||
                                                      field.value === undefined
                                                        ? ""
                                                        : String(field.value)
                                                    }
                                                    onChange={(e) => {
                                                      const raw =
                                                        e.target.value || "";
                                                      const digitsOnly =
                                                        raw.replace(
                                                          /[^0-9]/g,
                                                          "",
                                                        );
                                                      // Barcode may be scanned into a focused yantra input.
                                                      // If user already typed a yantra value (e.g. 5, 549) and then scans,
                                                      // the scan will append. So detect barcode as the last 12 digits.
                                                      const tail12 =
                                                        digitsOnly.length >= 12
                                                          ? digitsOnly.slice(
                                                              -12,
                                                            )
                                                          : "";
                                                      const foundBarcode =
                                                        isLikelySlipBarcode(
                                                          digitsOnly,
                                                        )
                                                          ? digitsOnly
                                                          : isLikelySlipBarcode(
                                                                tail12,
                                                              )
                                                            ? tail12
                                                            : "";

                                                      if (foundBarcode) {
                                                        form.setFieldValue(
                                                          field.name,
                                                          null,
                                                        );
                                                        lastYantraValueRef.current[
                                                          field.name
                                                        ] = "";
                                                        try {
                                                          e.target.value = "";
                                                        } catch (_) {}
                                                        setBarcodeInput(
                                                          foundBarcode,
                                                        );
                                                        Promise.resolve(
                                                          handleBarcodeSubmit(
                                                            foundBarcode,
                                                            {
                                                              clearInput: false,
                                                            },
                                                          ),
                                                        ).finally(() => {
                                                          setTimeout(() => {
                                                            focusFirstYantraInput();
                                                          }, 0);
                                                        });
                                                        return;
                                                      }

                                                      lastYantraValueRef.current[
                                                        field.name
                                                      ] = digitsOnly;
                                                      form.setFieldValue(
                                                        field.name,
                                                        digitsOnly,
                                                      );
                                                    }}
                                                    onClick={(e) =>
                                                      e.stopPropagation()
                                                    }
                                                    onKeyDown={(e) => {
                                                      if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        handleSubmit();
                                                        return;
                                                      }

                                                      const key = e.key;
                                                      if (
                                                        key !== "ArrowLeft" &&
                                                        key !== "ArrowRight" &&
                                                        key !== "ArrowUp" &&
                                                        key !== "ArrowDown"
                                                      ) {
                                                        return;
                                                      }

                                                      e.preventDefault();
                                                      e.stopPropagation();

                                                      const cols =
                                                        getYantraGridCols();
                                                      const total = (
                                                        values.yantra_list || []
                                                      ).length;

                                                      let next = idx;
                                                      if (key === "ArrowLeft")
                                                        next = idx - 1;
                                                      if (key === "ArrowRight")
                                                        next = idx + 1;
                                                      if (key === "ArrowUp")
                                                        next = idx - cols;
                                                      if (key === "ArrowDown")
                                                        next = idx + cols;

                                                      if (next < 0) next = 0;
                                                      if (next >= total)
                                                        next = total - 1;

                                                      setTimeout(
                                                        () =>
                                                          focusYantraInputAt(
                                                            next,
                                                          ),
                                                        0,
                                                      );
                                                    }}
                                                  />
                                                )}
                                              </Field>
                                            </div>
                                          </div>
                                        );
                                      },
                                    )}
                                  </div>
                                </div>
                              )}
                            </FieldArray>
                          )}
                        </div>

                        {/* Right: user ID bar + wallet panel + session panel (image 2/3) */}
                        <div className="outlet-right">
                          <div className="outlet-yantra-slideshow-wrap">
                            {slideshowSpotlight ? (
                              <>
                                <div
                                  className="fireworks outlet-yantra-spotlight-fireworks"
                                  aria-hidden
                                />
                                <div
                                  ref={containerRef}
                                  className="fireworks-container outlet-yantra-spotlight-fireworks-particles"
                                  aria-hidden
                                />
                              </>
                            ) : null}
                            <YantraSlideshow
                              yantras={yantraImages}
                              spotlightYantra={slideshowSpotlight}
                            />
                          </div>
                          <div className="text-center">
                            <input
                              type="text"
                              id="all-yantra-input-update"
                              name="all-yantra-input-update"
                              className="outlet-place-all-yantra-input-update"
                              placeholder="Apply in all yantras"
                              value={allYantraInputUpdate}
                              onChange={(e) => {
                                const raw = e.target.value || "";
                                const trimmed = String(raw).trim();
                                const digitsOnly = raw.replace(/[^0-9]/g, "");
                                // If a barcode gun scans while this input is focused,
                                // route it to barcode flow instead of applying digits to yantras.
                                const tail12 =
                                  digitsOnly.length >= 12
                                    ? digitsOnly.slice(-12)
                                    : "";
                                const foundBarcode = isLikelySlipBarcode(
                                  digitsOnly,
                                )
                                  ? digitsOnly
                                  : isLikelySlipBarcode(tail12)
                                    ? tail12
                                    : "";

                                if (foundBarcode) {
                                  setAllYantraInputUpdate("");
                                  setBarcodeInput(trimmed);
                                  Promise.resolve(
                                    handleBarcodeSubmit(trimmed, {
                                      clearInput: false,
                                    }),
                                  );
                                  return;
                                }

                                setAllYantraInputUpdate(digitsOnly);
                                (values.yantra_list || []).forEach((_, i) =>
                                  setFieldValue(
                                    `yantra_list[${i}].yantra_count`,
                                    digitsOnly,
                                  ),
                                );
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  handleSubmit();
                                  return;
                                }
                              }}
                            />
                            <button
                              type="button"
                              onClick={() =>
                                routerHistory.push("/place-bet-management")
                              }
                              className="outlet-place-bet-history-btn"
                            >
                              Place Bet History
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                routerHistory.push("/transaction-history")
                              }
                              className="outlet-transaction-history-btn"
                            >
                              Transaction History
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* <p className="outlet-disclaimer">
                    <span className="outlet-disclaimer-note">નોંધ:-</span>
                    {liveSessionNote2 ||
                      "Note: Yantra, Fengsui Item, and Rashi validity and purchase locations apply as per terms."}
                  </p> */}

                      <div className="game-controls">
                        <button
                          type="button"
                          className="game-btn"
                          onClick={() => setActiveTab("3min")}
                        >
                          <span className="key-box">F3</span>
                          <span className="btn-text">3 Min Yantra</span>
                        </button>

                        <button
                          type="button"
                          className="game-btn"
                          onClick={() => setActiveTab("5min")}
                        >
                          <span className="key-box">F5</span>
                          <span className="btn-text">5 Min Yantra</span>
                        </button>
                        <button
                          type="button"
                          className="game-btn"
                          onClick={fetchCurrentSessionPlaceBets}
                        >
                          <span className="key-box">F4</span>
                          <span className="btn-text">Cancel</span>
                        </button>

                        <button
                          type="button"
                          className="exit-btn"
                          onClick={handleBack}
                        >
                          <span className="exit-key-box">F11 Exit</span>
                        </button>

                        <button
                          type="button"
                          className="clear-btn"
                          onClick={() => {
                            (values.yantra_list || []).forEach((_, i) =>
                              setFieldValue(
                                `yantra_list[${i}].yantra_count`,
                                null,
                              ),
                            );
                            setTimeout(() => {
                              focusFirstYantraInput();
                            }, 0);
                          }}
                        >
                          <span className="key-box">F6</span>
                          <span className="clear-key-box">Clear</span>
                        </button>
                        <button
                          type="button"
                          className="clear-btn"
                          onClick={rePrintLastPlaceBet}
                        >
                          <span className="key-box">F7</span>
                          <span className="clear-key-box">Re-Print</span>
                        </button>

                        <button
                          type="submit"
                          className="place-btn"
                          disabled={isPlaceBetDisabled}
                        >
                          <span className="key-box light">Enter</span>
                          <span className="btn-text big">
                            {isSubmitting ? "Placing..." : "Place Bet"}
                          </span>
                        </button>
                        <div className="outlet-live-barcode-container">
                          <input
                            ref={barcodeInputRef}
                            type="text"
                            className="outlet-live-barcode-input"
                            placeholder="Enter bar code"
                            value={barcodeInput}
                            onChange={(e) => {
                              const v = e.target.value || "";
                              // Allow only digits / letters / _ / -
                              if (/^[0-9A-Za-z_-]*$/.test(v)) {
                                setBarcodeInput(v);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleBarcodeSubmit(e.currentTarget.value);
                              }
                            }}
                          />
                          <button
                            type="button"
                            className="outlet-live-barcode-button"
                            onClick={() => handleBarcodeSubmit()}
                          >
                            submit
                          </button>
                        </div>
                      </div>
                    </Form>
                  )}
                </YantraImagesLoader>
              );
            }}
          </Formik>
        </div>

        <div ref={footerRef} className="outlet-live-footer">
          {historyLoading ? (
            <Loader message="Loading Last Draw..." />
          ) : history.length === 0 ? (
            <NoData
              message="No Last Draw Available"
              subText="Please try again later"
            />
          ) : (
            <div className="outlet-history-section">
              <div className="d-flex justify-content-between align-items-center">
                <h3 className="outlet-history-title">Last Draw</h3>
                <button
                  type="button"
                  className="outlet-history-btn"
                  onClick={() => routerHistory.push("/all-draw")}
                >
                  All Draws
                </button>
              </div>

              <div className="outlet-history-grid">
                {history.flatMap((item, idx) => {
                  const endTime = item?.end_time
                    ? moment.utc(item.end_time).local()
                    : null;
                  const dateLabel = endTime ? endTime.format("DD-MM-YYYY") : "";
                  const timeLabel = endTime
                    ? endTime.format(TIME_DISPLAY_FORMAT)
                    : "";
                  const winnerType = String(
                    item?.winner_type || "",
                  ).toUpperCase();
                  const isJackpot = winnerType === "JACKPOT" ? true : false;
                  const jackpotBadgeLabel =
                    winnerType === "JACKPOT"
                      ? "Multi Winner"
                      : winnerType === "JACKPOTX"
                        ? (() => {
                            const raw = String(item?.winner_x || "").trim();
                            if (!raw) return "Jackpot";
                            if (/[xX]$/.test(raw)) return `Jackpot (${raw})`;
                            return `Jackpot (${raw}x)`;
                          })()
                        : null;
                  const winners =
                    Array.isArray(item?.winner_yantras) &&
                    item.winner_yantras.length > 0
                      ? item.winner_yantras
                      : [
                          {
                            yantra_name: item?.yantra_name,
                            yantra_img: item?.yantra_img,
                            yantra_id: item?.winner_yantra,
                          },
                        ];

                  if (isJackpot) {
                    return [
                      <div
                        key={`${item.session_id}-jackpot`}
                        className="neon-card"
                      >
                        <div className="neon-content">
                          <div className="outlet-live-session-winners-scroll">
                            {winners.map((w, wIdx) => {
                              const imgSrc = getYantraImage(
                                w?.yantra_img,
                                w?.yantra_id,
                              );
                              return (
                                <div
                                  key={`${item.session_id}-${w?.yantra_id ?? wIdx}`}
                                  className="outlet-live-session-winner-item"
                                >
                                  <div className="outlet-live-session-winner-item-img">
                                    <img
                                      src={imgSrc}
                                      alt={w?.yantra_name || "Yantra"}
                                    />
                                  </div>
                                  <div className="outlet-live-session-winner-item-name">
                                    {w?.yantra_name || "Yantra"}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          <hr className="outlet-history-divider" />
                          <div>
                            <div className="multi-winner-bar">
                              <span>MULTI WINNER</span>
                            </div>
                            <div className="outlet-multi-winner-date">
                              <span>
                                {dateLabel} {timeLabel}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>,
                    ];
                  }

                  return winners.map((w, wIdx) => {
                    const nameLabel =
                      w?.yantra_name ||
                      `Yantra ${w?.yantra_id || ""}` ||
                      "Yantra";
                    const imgSrc = getYantraImage(
                      w?.yantra_img,
                      w?.yantra_id,
                    );
                    return winnerType === "JACKPOTX" ? (
                      <>
                        <div key={`${item.session_id}-${w?.yantra_id ?? wIdx}`}>
                          <div className="outlet-live-jackpot-screen">
                            <div className="outlet-live-jackpot-card">
                              <div
                                className="outlet-live-jackpot-content"
                                style={{
                                  "--outlet-jackpot2-bg": `url(${JackpotImage2})`,
                                }}
                              >
                                <div className="outlet-history-top">
                                  <div className="outlet-live-jackpot-yantra">
                                    <img
                                      loading="lazy"
                                      src={imgSrc}
                                      alt={nameLabel}
                                    />
                                  </div>
                                  <div
                                    className={`outlet-history-badge jackpotx`}
                                    title={`jackpotx`}
                                  >
                                    <div className="outlet-history-badge-img-wrap">
                                      <img
                                        src={JackpotImage}
                                        alt=""
                                        className="outlet-history-badge-brand"
                                        decoding="async"
                                      />
                                    </div>
                                    <div className="outlet-history-badge-copy">
                                      <span className="outlet-history-badge-sub">
                                        {jackpotBadgeLabel}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <div className="outlet-live-jackpot-footer">
                                  <div className="outlet-live-jackpot-footer-yantra">
                                    {nameLabel}
                                  </div>
                                  <div className="outlet-live-jackpot-footer-time">
                                    {dateLabel} {timeLabel}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div
                          key={`${item.session_id}-${w?.yantra_id ?? wIdx}`}
                          className={`outlet-history-card outlet-history-card-variant-${(idx + wIdx) % 12}`}
                        >
                          <div className="outlet-history-top">
                            <div className="outlet-history-icon-wrap">
                              {imgSrc ? (
                                <img
                                  loading="lazy"
                                  src={imgSrc}
                                  alt={nameLabel}
                                />
                              ) : (
                                <div className="outlet-history-icon-placeholder" />
                              )}
                            </div>
                            <div className="outlet-history-datetime">
                              <div className="outlet-history-date">
                                {dateLabel}
                              </div>
                              <div className="outlet-history-time">
                                {timeLabel}
                              </div>
                            </div>
                          </div>
                          <hr className="outlet-history-divider" />
                          <div className="outlet-history-name">{nameLabel}</div>
                        </div>
                      </>
                    );
                  });
                })}
              </div>
            </div>
          )}
        </div>
      </div>
      {placeBetListOpen && (
        <div className="outlet-placebet-panel">
          <div className="outlet-placebet-panel-inner">
            <div className="outlet-placebet-header">
              <h3>Current Session Place Bets</h3>
              <button
                type="button"
                className="outlet-placebet-close"
                onClick={() => setPlaceBetListOpen(false)}
              >
                <AiOutlineClose />
              </button>
            </div>
            {placeBetListLoading ? (
              <Loader message="Loading place bets..." />
            ) : placeBetList.length === 0 ? (
              <NoData
                message="No place bets in this session"
                subText="Bets will appear here once placed"
              />
            ) : (
              <div className="outlet-placebet-list">
                <div className="outlet-placebet-thead">
                  <span>Barcode</span>
                  {/* <span>User</span> */}
                  <span>Bet</span>
                  <span>Amount</span>
                  <span>Time</span>
                  <span>Draw</span>
                  <span>Action</span>
                </div>
                {placeBetList.map((bet, idx) => (
                  <div key={bet.id} className="outlet-placebet-row">
                    {/* <span className="outlet-placebet-user">
                      {bet.user_name || bet.user_username || `#${bet.user_id}`}
                    </span> */}
                    <span
                      className="outlet-placebet-uid"
                      title={bet.placebet_uid || undefined}
                    >
                      {/* {shortenPlaceBetUid(bet.placebet_uid)} */}
                      {bet.bar_code || shortenPlaceBetUid(bet.placebet_uid)}
                    </span>
                    <span className="outlet-placebet-total-bet">
                      {bet.total_bet}
                    </span>
                    <span className="outlet-placebet-amount">
                      ₹{Number(bet.amount || 0).toFixed(2)}
                    </span>
                    <span className="outlet-placebet-current-time">
                      {bet.current_time || "—"}
                    </span>
                    <span className="outlet-placebet-draw-time">
                      {bet.draw_time || "—"}
                    </span>
                    <span className="outlet-placebet-action">
                      <button
                        type="button"
                        className="outlet-placebet-print-btn"
                        onClick={() => openPrintView(bet)}
                      >
                        <FaPrint />
                      </button>
                      {placeBetList.length === idx + 1 &&
                        Number(bet?.isScan) === 0 && (
                          <button
                            type="button"
                            className="outlet-placebet-cancel-btn"
                            disabled={
                              cancelSubmitting || Number(bet?.isScan) === 1
                            }
                            onClick={() => openCancelConfirm(bet)}
                          >
                            Cancel
                          </button>
                        )}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div
            className="outlet-placebet-overlay"
            onClick={() => setPlaceBetListOpen(false)}
          />
        </div>
      )}

      {cancelConfirmOpen &&
        cancelTargetBet &&
        createPortal(
          <div
            className="outlet-cancel-confirm-overlay"
            role="dialog"
            aria-modal="true"
            onClick={closeCancelConfirm}
          >
            <div
              className="outlet-cancel-confirm-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="outlet-cancel-confirm-title">
                Cancel Confirmation
              </div>
              <div className="outlet-cancel-confirm-text">
                Are you sure you want to cancel this bet?
              </div>
              <div className="outlet-cancel-confirm-meta">
                <div>
                  <strong>Barcode:</strong>{" "}
                  {cancelTargetBet.bar_code ||
                    shortenPlaceBetUid(cancelTargetBet.placebet_uid)}
                </div>
                <div>
                  <strong>Amount:</strong> ₹
                  {Number(cancelTargetBet.amount || 0).toFixed(2)}
                </div>
              </div>
              <div className="outlet-cancel-confirm-actions">
                <button
                  type="button"
                  className="outlet-cancel-confirm-btn outlet-cancel-no"
                  onClick={closeCancelConfirm}
                  disabled={cancelSubmitting}
                >
                  No
                </button>
                <button
                  type="button"
                  className="outlet-cancel-confirm-btn outlet-cancel-yes"
                  onClick={confirmCancelPlaceBet}
                  disabled={cancelSubmitting}
                >
                  {cancelSubmitting ? "Cancelling..." : "Yes, Cancel"}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {printViewOpen &&
        selectedPlaceBet &&
        createPortal(
          <div className="outlet-print-view-wrap">
            <div className="outlet-print-view no-print-hide">
              <div className="outlet-print-header">
                <div> </div>
                <div className="outlet-print-actions">
                  <button
                    type="button"
                    className="outlet-print-btn"
                    onClick={handlePrint}
                  >
                    Print
                  </button>
                  <button
                    type="button"
                    className="outlet-print-close"
                    onClick={() => {
                      setPrintViewOpen(false);
                      setSelectedPlaceBet(null);
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
              <div className="outlet-print-body">
                <div className="outlet-print-body-main_title">
                  <h3 className="outlet-print-body-main_title_text">
                    {settingData?.main_title || "Yantra"} [
                    {selectedPlaceBet.game_type || "—"}]
                  </h3>
                  <div className="outlet-print-body-main_title_sub">
                    <span>Receipt No: </span>
                    <span>
                      {stripReceiptPrefix(
                        selectedPlaceBet?.bar_code ||
                          selectedPlaceBet?.placebet_uid,
                      )}
                    </span>
                  </div>
                  <div className="outlet-print-body-main_title_sub2">
                    <span>User: </span>
                    <span>{slipOwnerDisplay(selectedPlaceBet)}</span>
                  </div>
                  <div className="outlet-print-body-main_title_sub2">
                    <span>
                      Purchase Time:{" "}
                      {selectedPlaceBet.created_at
                        ? formatSlipDateTime(selectedPlaceBet.created_at)
                        : "—"}
                    </span>
                  </div>
                  <div className="outlet-print-body-main_title_sub2">
                    <span>Draw Time</span>
                    <span>
                      {selectedPlaceBet.current_time
                        ? selectedPlaceBet.current_time
                        : "—"}
                      -
                      {selectedPlaceBet.draw_time
                        ? selectedPlaceBet.draw_time
                        : "—"}
                    </span>
                  </div>
                </div>

                {Array.isArray(selectedPlaceBet.yantra_list) &&
                  selectedPlaceBet.yantra_list.length > 0 && (
                    <div className="outlet-print-yantras">
                      <table className="outlet-print-yantra-table">
                        <thead>
                          <tr>
                            <th>Yantra</th>
                            <th>Qty</th>
                            <th>Price</th>
                            <th>Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedPlaceBet.yantra_list.map((y, idx) => {
                            const qty = Number(y.yantra_count || 0);
                            const basePrice = Number(y.yantra_amount || 0);
                            const total = basePrice * qty;
                            return (
                              <tr key={idx}>
                                <td>
                                  {y.yantra_name || `Yantra ${y.yantra_id}`}
                                </td>
                                <td>{qty}</td>
                                <td>{basePrice}</td>
                                <td>{total}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td>
                              <span className="outlet-print-yantra-tfoot-total">
                                Total
                              </span>
                            </td>
                            <td className="outlet-print-yantra-tfoot-qty">
                              {selectedPlaceBet.yantra_list.reduce(
                                (sum, y) => sum + Number(y.yantra_count || 0),
                                0,
                              )}
                            </td>
                            <td />
                            <td>
                              <span className="outlet-print-yantra-tfoot-total">
                                {Number(selectedPlaceBet.amount || 0).toFixed(
                                  2,
                                )}
                              </span>
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}

                <div className="outlet-print-qr">
                  {selectedPlaceBet?.bar_code ||
                  selectedPlaceBet?.placebet_uid ? (
                    <>
                      <img
                        src={`https://barcode.tec-it.com/barcode.ashx?data=${encodeURIComponent(
                          selectedPlaceBet?.bar_code ||
                            selectedPlaceBet?.placebet_uid,
                        )}&code=Code128`}
                        alt="Receipt barcode"
                        style={{ width: "100%", height: "auto" }}
                        className="outlet-print-barcode-img"
                      />
                      <div className="outlet-print-uid outlet-print-uid-print-only">
                        {stripReceiptPrefix(
                          selectedPlaceBet?.bar_code ||
                            selectedPlaceBet?.placebet_uid,
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="outlet-print-qr-fallback">Barcode N/A</div>
                  )}
                </div>
              </div>
            </div>
            <div
              className="outlet-print-overlay"
              onClick={() => {
                setPrintViewOpen(false);
                setSelectedPlaceBet(null);
              }}
            />
          </div>,
          document.body,
        )}
      {scanResultModalOpen &&
        scanResultData &&
        createPortal(
          <div
            className="scan-win-modal-overlay"
            role="dialog"
            aria-modal="true"
            onClick={closeScanResultModal}
          >
            <div
              className="scan-win-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="scan-win-close-btn"
                onClick={closeScanResultModal}
              >
                <AiOutlineClose />
              </button>
              {scanResultData.totalWinAmount != null && (
                <div className="scan-win-gif-wrap">
                  <img
                    //C:\Edrive\yantra-game\client\src\assets\tenor.gif
                    src={tenorGif}
                    alt="Congratulations"
                  />
                </div>
              )}
              <h2 className="scan-win-title">
                {scanResultData.totalWinAmount != null
                  ? "Congratulations!"
                  : "Message"}
              </h2>
              <p className="scan-win-message">
                {scanResultData.message || "You have won the following prize."}
              </p>
              {scanResultData.totalWinAmount != null && (
                <>
                  <div className="scan-win-amount-box">
                    <div className="scan-win-you-win-silver-text-wrap">
                      <span className="scan-win-you-win-silver-text">
                        You have win {silverCoinsFromWinPayload(scanResultData)}{" "}
                        silver coins
                        <span className="scan-win-you-win-silver-coin-img">
                          <img src={silverCoinGif} alt="Silver Coin" />
                        </span>
                      </span>
                    </div>
                    <div className="scan-win-or-text">
                      -------------OR-------------
                    </div>
                    <span className="scan-win-amount-label">
                      Winning Amount
                    </span>
                    <span className="scan-win-amount-value">
                      ₹{Number(scanResultData.totalWinAmount || 0).toFixed(2)}
                    </span>
                    <div className="scan-win-you-win-silver-note">
                      <span className="scan-win-disclaimer-note">નોંધ:-</span>
                      <span className="scan-win-disclaimer-note-text">
                        {winningSlipNote}
                      </span>
                    </div>
                  </div>
                  <div className="scan-win-details">
                    <div className="scan-win-detail-row">
                      <span className="label">Winner Yantra</span>
                      <span className="value">
                        {scanResultData?.winnerYantra === "MULTIPLE"
                          ? "MULTIPLE"
                          : scanResultData.winnerYantraName ||
                            `#${scanResultData.winnerYantraId || "—"}`}
                      </span>
                    </div>
                    {Array.isArray(scanResultData?.winnerYantraDetails) &&
                      scanResultData.winnerYantraDetails.length > 0 && (
                        <div style={{ marginTop: 6 }}>
                          {scanResultData.winnerYantraDetails.map((w, i) => (
                            <div
                              key={`${w?.winnerYantraId || i}-${i}`}
                              className="scan-win-detail-row"
                            >
                              <span className="label">
                                {w?.winnerYantraName ||
                                  `#${w?.winnerYantraId || "—"}`}
                              </span>
                              <span className="value">
                                {Number(w?.winnerYantraCount || 0)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    {scanResultData?.winnerYantra !== "MULTIPLE" &&
                      scanResultData.winnerYantraImg && (
                        <div className="scan-win-yantra-img-row">
                          <img
                            loading="lazy"
                            src={getYantraImage(
                              scanResultData.winnerYantraImg,
                              scanResultData.winnerYantraId,
                            )}
                            alt={
                              scanResultData.winnerYantraName || "Winner yantra"
                            }
                          />
                        </div>
                      )}
                    <div className="scan-win-detail-row">
                      <span className="label">Winner Type</span>
                      <span className="value">
                        {scanResultData.winnerType || "—"}
                      </span>
                    </div>
                    <div className="scan-win-detail-row">
                      <span className="label">Total Bet</span>
                      <span className="value">
                        {scanResultData.totalBet ?? "—"}
                      </span>
                    </div>
                    <div className="scan-win-detail-row">
                      <span className="label">New Wallet Balance</span>
                      <span className="value">
                        ₹
                        {Number(
                          scanResultData.finalWalletBalance ??
                            initialValues.wallet_balance ??
                            0,
                        ).toFixed(2)}
                      </span>
                    </div>
                    {scanResultData.scannedCode && (
                      <div className="scan-win-detail-row">
                        <span className="label">Scanned Code</span>
                        <span className="value">
                          {stripReceiptPrefix(scanResultData.scannedCode)}
                        </span>
                      </div>
                    )}
                  </div>
                </>
              )}
              <button
                type="button"
                className="scan-win-ok-btn"
                onClick={closeScanResultModal}
              >
                OK
              </button>
            </div>
          </div>,
          document.body,
        )}
      {winPrintOpen &&
        winPrintData &&
        createPortal(
          <div className="outlet-win-print-view-wrap">
            <div className="outlet-win-print-view no-print-hide">
              <div className="outlet-win-print-body">
                <div className="outlet-win-print-title">
                  {settingData?.main_title || "Yantra"} [
                  {winPrintData.game_type || "—"}]
                </div>
                <div className="outlet-win-print-sub">
                  <span>Receipt No : </span>
                  <span>
                    {stripReceiptPrefix(
                      winPrintData?.bar_code || winPrintData?.placebet_uid,
                    )}
                  </span>
                </div>
                <div className="outlet-win-print-sub2">
                  <span>User: {slipOwnerDisplay(winPrintData)}</span>
                </div>
                <div className="outlet-win-print-sub2">
                  <span>
                    Purchase Time:{" "}
                    {winPrintData?.created_at
                      ? formatSlipDateTime(winPrintData.created_at)
                      : "—"}
                  </span>
                </div>
                <div className="outlet-win-print-sub2">
                  <span>
                    Draw Time:{" "}
                    {winPrintData?.current_time
                      ? winPrintData.current_time
                      : "—"}
                    -{winPrintData?.draw_time ? winPrintData.draw_time : "—"}
                  </span>
                </div>

                <div className="outlet-win-print-line" />

                {Array.isArray(winPrintData?.winnerYantraDetails) &&
                winPrintData.winnerYantraDetails.length > 0 ? (
                  <>
                    {winPrintData.winnerYantraDetails.map((w, i) => (
                      <div
                        className="outlet-win-print-row"
                        key={`${w?.winnerYantraId || i}-${i}`}
                      >
                        <span className="name">
                          {w?.winnerYantraName ||
                            `#${w?.winnerYantraId || "—"}`}
                        </span>
                        <span className="count">
                          {Number(w?.winnerYantraCount || 0)}
                        </span>
                        <span className="unit">
                          * {Number(winPrintData?.winUnitAmount || 0)}
                        </span>
                        <span className="eq">=</span>
                        <span className="total">
                          {Number(w?.winnerYantraCount || 0) *
                            Number(winPrintData?.winUnitAmount || 0)}
                        </span>
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="outlet-win-print-row">
                    <span className="name">
                      {winPrintData?.winnerYantraName || "—"}
                    </span>
                    <span className="count">
                      {Number(winPrintData?.winYantraCount || 0)}
                    </span>
                    <span className="unit">
                      * {Number(winPrintData?.winUnitAmount || 0)}
                    </span>
                    <span className="eq">=</span>
                    <span className="total">
                      {Number(winPrintData?.totalWinAmount || 0)}
                    </span>
                  </div>
                )}

                <div className="outlet-win-print-line" />

                <div className="outlet-win-print-total">
                  <span className="name">Total:</span>
                  <span className="total">
                    {Number(winPrintData?.totalWinAmount || 0)}
                  </span>
                </div>
                <div className="win-print-or-text">
                  -------------OR-------------
                </div>
                <div className="outlet-win-print-you-win-silver">
                  <span className="outlet-win-print-you-win-silver-text">
                    You have win {silverCoinsFromWinPayload(winPrintData)}{" "}
                    silver coins
                  </span>
                </div>

                <div className="outlet-win-print-you-win">YOU WIN</div>
                <div className="outlet-win-print-you-win-silver-note">
                  <span className="outlet-win-print-disclaimer-note">
                    નોંધ:-
                  </span>
                  <span className="outlet-win-print-disclaimer-note-text">
                    {winningSlipNote}
                  </span>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default OutletLiveSession;
